import { UserCogIcon, CheckCircle2Icon, XCircleIcon } from 'lucide-react';
import type { AssignedJob, Customer } from '@repo/types';
import { SectionCard, FieldRow, ToneBadge, DescriptionList } from './_shared';
import { CustomerAssignedJobs } from './CustomerAssignedJobs';
import { getJobSearchStatusTone, humanize } from './utils';

export function CustomerAccountTab({
  customer,
  assignedJobs,
  isLoadingJobs,
  onAssignJobClick,
  formattedDates,
}: {
  customer: Customer;
  assignedJobs: AssignedJob[];
  isLoadingJobs: boolean;
  onAssignJobClick: () => void;
  formattedDates: {
    created: string | null;
    updated: string | null;
    lastProfileUpdate: string | null;
    verification: string | null;
  } | null;
}) {
  return (
    <div className="space-y-5">
      <CustomerAssignedJobs
        assignedJobs={assignedJobs}
        isLoading={isLoadingJobs}
        onAssignJobClick={onAssignJobClick}
      />

      <SectionCard icon={UserCogIcon} title="Account & System">
        <div className="space-y-4">
          <DescriptionList
            items={[
              { label: 'Customer ID', value: <span className="break-all font-mono text-xs">{customer.id}</span> },
              {
                label: 'Subscription ID',
                value: customer.subscriptionId ? (
                  <span className="break-all font-mono text-xs">{customer.subscriptionId}</span>
                ) : null,
              },
            ]}
          />

          <div className="divide-y divide-border/60">
            {customer.jobSearchStatus && (
              <FieldRow label="Job search status">
                <ToneBadge tone={getJobSearchStatusTone(customer.jobSearchStatus)}>
                  {humanize(customer.jobSearchStatus)}
                </ToneBadge>
              </FieldRow>
            )}
            {customer.profileVisibility && (
              <FieldRow label="Profile visibility">{humanize(customer.profileVisibility)}</FieldRow>
            )}
            {customer.profileVerified !== undefined && (
              <FieldRow label="Verification">
                <span className="inline-flex items-center gap-1.5">
                  {customer.profileVerified ? (
                    <>
                      <CheckCircle2Icon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden />
                      Verified
                      {formattedDates?.verification && (
                        <span className="text-xs text-muted-foreground">({formattedDates.verification})</span>
                      )}
                    </>
                  ) : (
                    <>
                      <XCircleIcon className="h-4 w-4 text-muted-foreground" aria-hidden />
                      Not verified
                    </>
                  )}
                </span>
              </FieldRow>
            )}
            {formattedDates?.created && <FieldRow label="Account created">{formattedDates.created}</FieldRow>}
            {formattedDates?.updated && <FieldRow label="Last updated">{formattedDates.updated}</FieldRow>}
            {formattedDates?.lastProfileUpdate && (
              <FieldRow label="Last profile update">{formattedDates.lastProfileUpdate}</FieldRow>
            )}
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
